import React, { useEffect, useState } from "react";
import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { teamApi } from "api/team";
import { getCurrentUser } from "api/client";

const ROLES = ["Owner", "Manager", "Rep", "ReadOnly"];

function RoleSelect({ value, onChange, disabled, allowed }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="text-sm px-2 py-1.5 rounded-lg border font-caption"
      style={{
        borderColor: "var(--color-border)",
        background: "var(--color-card)",
        color: "var(--color-foreground)",
        height: 34,
        maxWidth: 130,
      }}
      aria-label="Role"
    >
      {(allowed || ROLES).map((r) => (
        <option key={r} value={r}>{r}</option>
      ))}
    </select>
  );
}

export default function Team() {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [formError, setFormError] = useState("");
  const [inviteResult, setInviteResult] = useState(null);
  const [isInviting, setIsInviting] = useState(false);
  const [roleBusy, setRoleBusy] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ fullName: "", email: "", role: "Rep" });

  const currentUser = getCurrentUser();
  const isOwner = currentUser?.role === "Owner";
  const isManager = currentUser?.role === "Manager";
  const canManage = isOwner || isManager;

  const load = async () => {
    try {
      const items = await teamApi.listMembers();
      setMembers(items);
      setLoadError("");
    } catch (error) {
      setLoadError(error?.message || "Unable to load team.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) load();
  }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setFormError("");
    setInviteResult(null);
    setIsInviting(true);
    try {
      const result = await teamApi.inviteMember(form);
      setInviteResult(result);
      setForm({ fullName: "", email: "", role: "Rep" });
      await load();
    } catch (error) {
      setFormError(error?.message || "Unable to send invite.");
    } finally {
      setIsInviting(false);
    }
  };

  const handleRoleChange = async (memberId, role) => {
    setRoleBusy(memberId);
    try {
      await teamApi.updateRole(memberId, role);
      await load();
    } catch (error) {
      setLoadError(error?.message || "Unable to change role.");
    } finally {
      setRoleBusy(null);
    }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`Remove ${member.name} from the workspace?`)) return;
    setRemoveBusy(member.id);
    try {
      await teamApi.removeMember(member.id);
      await load();
    } catch (error) {
      setLoadError(error?.message || "Unable to remove member.");
    } finally {
      setRemoveBusy(null);
    }
  };

  // A Manager manages only Rep/ReadOnly; the Owner manages everything.
  const grantable = isOwner ? ROLES : ["Rep", "ReadOnly"];
  const isManaged = (member) =>
    isOwner || (isManager && ["Rep", "ReadOnly"].includes(member.role));

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        <div className="mb-6">
          <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}>
            Team
          </h1>
          <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
            Invite teammates — each invite link sets the member's password (72h expiry)
          </p>
        </div>

        {!canManage ? (
          <div className="rounded-xl border p-8 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>
              Only the Owner or a Manager can manage the team.
            </p>
          </div>
        ) : (
          <>
            {loadError && (
              <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
                <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{loadError}</p>
              </div>
            )}

            {/* Invite form */}
            <div className="rounded-xl border p-4 mb-6" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
              <h2 className="font-heading font-semibold text-lg mb-3" style={{ color: "var(--color-foreground)" }}>Invite a member</h2>
              <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3">
                <input
                  className="flex-1 text-sm px-3 py-2 rounded-lg border font-caption"
                  style={{ borderColor: "var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)" }}
                  placeholder="Full name"
                  value={form.fullName}
                  onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                  required
                />
                <input
                  className="flex-1 text-sm px-3 py-2 rounded-lg border font-caption"
                  style={{ borderColor: "var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)" }}
                  placeholder="Email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
                <RoleSelect
                  value={form.role}
                  allowed={grantable}
                  onChange={(role) => setForm((f) => ({ ...f, role }))}
                />
                <Button type="submit" disabled={isInviting} size="default">
                  <Icon name="UserPlus" size={14} style={{ marginRight: 6 }} />
                  {isInviting ? "Sending…" : "Send invite"}
                </Button>
              </form>

              {formError && (
                <p className="font-caption text-sm mt-3" style={{ color: "var(--color-error)" }}>{formError}</p>
              )}

              {inviteResult && (
                <div className="mt-3 rounded-lg border px-4 py-3" style={{ borderColor: "#BBF7D0", background: "#F0FDF4" }}>
                  <p className="font-caption text-sm font-medium" style={{ color: "#15803D" }}>
                    Invite sent to {inviteResult.member.name}. Share this link (valid 72h):
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      readOnly
                      value={inviteResult.inviteUrl}
                      className="flex-1 text-xs px-2 py-1.5 rounded border font-caption"
                      style={{ borderColor: "var(--color-border)", background: "#fff", color: "var(--color-foreground)" }}
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          await navigator.clipboard?.writeText(inviteResult.inviteUrl);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        } catch {
                          setFormError("Could not copy — select the link manually.");
                        }
                      }}
                    >
                      <Icon name="Copy" size={13} style={{ marginRight: 5 }} />
                      {copied ? "Copied!" : "Copy"}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Members table */}
            <div className="rounded-xl border p-4" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
              <h2 className="font-heading font-semibold text-lg mb-3" style={{ color: "var(--color-foreground)" }}>
                Members ({members.length})
              </h2>
              {isLoading ? (
                <p className="font-caption text-sm py-4" style={{ color: "var(--color-text-secondary)" }}>Loading…</p>
              ) : members.length === 0 ? (
                <p className="font-caption text-sm py-4" style={{ color: "var(--color-text-secondary)" }}>No members yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="font-caption" style={{ color: "var(--color-text-secondary)" }}>
                        <th className="text-left py-1.5 pr-3 font-medium">Name</th>
                        <th className="text-left py-1.5 px-2 font-medium">Email</th>
                        <th className="text-left py-1.5 px-2 font-medium">Role</th>
                        <th className="text-left py-1.5 px-2 font-medium">Status</th>
                        <th className="text-left py-1.5 pl-2 font-medium">Joined</th>
                        <th className="text-right py-1.5 pl-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => (
                        <tr key={m.id} className="border-t" style={{ borderColor: "var(--color-border)" }}>
                          <td className="py-2 pr-3">
                            <span className="font-caption font-medium" style={{ color: "var(--color-foreground)" }}>{m.name}</span>
                            {m.id === currentUser?.sub && (
                              <span className="ml-2 text-[11px] font-caption" style={{ color: "var(--color-text-secondary)" }}>(you)</span>
                            )}
                          </td>
                          <td className="py-2 px-2 font-caption">{m.email}</td>
                          <td className="py-2 px-2">
                            {isManaged(m) ? (
                              <RoleSelect
                                value={m.role}
                                allowed={grantable}
                                disabled={roleBusy === m.id || (m.id === currentUser?.sub && isOwner)}
                                onChange={(role) => handleRoleChange(m.id, role)}
                              />
                            ) : (
                              <span className="font-caption">{m.role}</span>
                            )}
                          </td>
                          <td className="py-2 px-2">
                            {m.inviteStatus === "pending" ? (
                              <span className="inline-block px-1.5 py-0.5 rounded-full font-caption text-[11px] font-medium" style={{ background: "#FEF3C7", color: "#B45309" }}>
                                Pending invite
                              </span>
                            ) : (
                              <span className="inline-block px-1.5 py-0.5 rounded-full font-caption text-[11px] font-medium" style={{ background: "#DCFCE7", color: "#15803D" }}>
                                Active
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-2 font-caption" style={{ color: "var(--color-text-secondary)" }}>
                            {m.createdAtUtc ? new Date(m.createdAtUtc).toLocaleDateString("en-IN") : "—"}
                          </td>
                          <td className="py-2 pl-2 text-right">
                            {isOwner && m.id !== currentUser?.sub && (
                              <Button variant="ghost" size="sm" loading={removeBusy === m.id} onClick={() => handleRemove(m)}>
                                <Icon name="Trash2" size={13} style={{ marginRight: 5 }} />
                                {removeBusy === m.id ? "Removing…" : "Remove"}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
